// src/pages/TherapistPage/CaseHistory/WithdrawalQueue.js
//
// Deciding a withdrawal request.
//
// The request already stopped new recordings the day the family asked. What
// waits here is **deletion**, and deletion cannot be undone — so the screen is
// built around two things.
//
// Nobody approves blind: opening a request shows exactly what would be removed,
// counted live, before any button appears. And whoever raised the request does
// not decide it, except in a clinic with a single therapist, where the screen
// says plainly that nobody else reviewed it.
//
// The third tab is the work that outlives the deletion. A child whose data had
// already gone out in a bundle cannot be erased from it from here; their code
// stays on a list until somebody purges those copies.
import React, { useCallback, useEffect, useState } from 'react';
import {
    decide, getPlan, listRequests, listWithdrawn, withdrawalError,
} from './withdrawalApi';

const TABS = [
    { key: 'pending', label: 'Waiting' },
    { key: 'rejected', label: 'Refused' },
    { key: 'purge', label: 'To purge from bundles' },
];

// Spelled out rather than left to toLocaleDateString, which renders "Oct 20"
// on a machine set to US English — the wrong way round for everyone reading
// these screens, and ambiguous in a record that has to be read years later.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const when = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

export default function WithdrawalQueue() {
    const [tab, setTab] = useState('pending');
    // The rows are stored with the tab they belong to. Keeping them apart let a
    // tab switch render the new tab's table over the old tab's rows for one
    // frame — and a withdrawal request has none of the fields a purge row does.
    const [loaded, setLoaded] = useState(null);
    const [plan, setPlan] = useState(null);
    const [note, setNote] = useState('');
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const load = useCallback(() => {
        setError('');
        const call = tab === 'purge' ? listWithdrawn() : listRequests(tab);
        return call
            .then(({ data }) => { setLoaded({ tab, rows: data }); setPlan(null); setConfirming(false); })
            .catch((err) => setError(withdrawalError(err, 'Could not load this list.')));
    }, [tab]);

    useEffect(() => { load(); }, [load]);

    const open = async (row) => {
        setBusy(true); setError(''); setMessage(''); setNote(''); setConfirming(false);
        try {
            const { data } = await getPlan(row.id);
            setPlan(data);
        } catch (err) {
            setError(withdrawalError(err, 'Could not work out what would be deleted.'));
        } finally { setBusy(false); }
    };

    const send = async (status) => {
        setBusy(true); setError(''); setMessage('');
        try {
            const { data } = await decide(plan.request_id, {
                status,
                note: note.trim() || null,
                confirm_deletion: status === 'approved',
            });
            setMessage(data.detail || 'Done.');
            setPlan(null);
            await load();
        } catch (err) {
            setError(withdrawalError(err, 'Could not record that decision.'));
        } finally { setBusy(false); }
    };

    const fresh = loaded && loaded.tab === tab;
    if (error && !fresh) return <div className="cf-note red">{error}</div>;
    if (!fresh) {
        return (
            <div>
                <Filters tab={tab} onTab={setTab} />
                <div className="cf-empty">Loading…</div>
            </div>
        );
    }
    const rows = loaded.rows;

    // ── one request, with what approving it would destroy ─────────────────
    if (plan) {
        const rowCounts = Object.entries(plan.rows || {}).filter(([, n]) => n > 0);
        const fileCounts = Object.entries(plan.blobs || {}).filter(([, n]) => n > 0);

        return (
            <div>
                {error && <div className="cf-note red">{error}</div>}

                <div className="cf-bar">
                    <button type="button" className="btn btn-sm btn-secondary" onClick={() => setPlan(null)}>
                        ← Back to the queue
                    </button>
                </div>

                <div className="cf-block">
                    <h4>{plan.child_name}</h4>
                    <p className="cf-hint">
                        Asked for by {plan.requested_by_name || 'someone no longer on file'} on {when(plan.created_at)}.
                    </p>
                    <div className="cf-note blue"><b>Their reason:</b> {plan.reason}</div>
                </div>

                <div className="cf-block">
                    <h4>What approving would delete</h4>
                    {!plan.storage_available ? (
                        <div className="cf-note red">
                            <b>Storage could not be reached.</b> {plan.storage_error}
                            <div className="cf-hint">
                                Approving is refused while the recordings cannot be reached. Deleting
                                the rows now would leave the audio behind while the record claimed
                                the data was erased. Try again once storage is back.
                            </div>
                        </div>
                    ) : (rowCounts.length === 0 && fileCounts.length === 0) ? (
                        <p className="cf-hint">Nothing recorded for this child yet — only the child&apos;s own details.</p>
                    ) : (
                        <table className="cf-measures">
                            <thead><tr><th>What</th><th>How many</th></tr></thead>
                            <tbody>
                                {rowCounts.map(([name, n]) => (
                                    <tr key={name}><td>{name.replace(/_/g, ' ')}</td><td>{n}</td></tr>
                                ))}
                                {fileCounts.map(([name, n]) => (
                                    <tr key={name}><td>{name}</td><td>{n}</td></tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {plan.research_subject && (
                        <div className="cf-note amber">
                            <b>This child is in the research dataset as {plan.research_subject}.</b>
                            <div className="cf-hint">
                                Approving keeps that code on the purge list, so the bundles already
                                handed out can be cleaned. Afterwards the code is all that is left —
                                nothing here will point back to a child again.
                            </div>
                        </div>
                    )}
                </div>

                <div className="cf-block">
                    <div className="cf-field">
                        <label htmlFor="wq-note">Note</label>
                        <textarea
                            id="wq-note" rows="3" value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="What you checked, or why the request is being refused."
                        />
                    </div>

                    {!plan.may_decide ? (
                        <div className="cf-note red">{plan.may_decide_reason}</div>
                    ) : (
                        <>
                            {plan.sole_reviewer && (
                                <div className="cf-note amber">
                                    You are the only therapist in this organisation, so you may decide
                                    your own request. The record will say that nobody else reviewed it.
                                </div>
                            )}
                            {confirming ? (
                                <div className="cf-note red">
                                    <b>Delete every record for {plan.child_name}?</b>
                                    <div className="cf-hint">
                                        This cannot be undone. The request itself goes too — its reason
                                        names the family — and the erasure log becomes the only record
                                        that any of this happened.
                                    </div>
                                    <div className="cf-actions">
                                        <button
                                            type="button" className="btn btn-primary" disabled={busy}
                                            onClick={() => send('approved')}
                                        >
                                            {busy ? 'Deleting…' : 'Yes, delete the records'}
                                        </button>
                                        <button
                                            type="button" className="btn btn-secondary" disabled={busy}
                                            onClick={() => setConfirming(false)}
                                        >
                                            Not yet
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="cf-actions">
                                        <button
                                            type="button" className="btn btn-primary"
                                            disabled={busy || !plan.storage_available}
                                            onClick={() => setConfirming(true)}
                                        >
                                            Approve and delete…
                                        </button>
                                        <button
                                            type="button" className="btn btn-secondary"
                                            disabled={busy || !note.trim()}
                                            onClick={() => send('rejected')}
                                        >
                                            Refuse this request
                                        </button>
                                    </div>
                                    {!note.trim() && (
                                        <p className="cf-hint">
                                            Refusing needs a reason written here: a family asked for
                                            something and is being told no.
                                        </p>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        );
    }

    // ── the purge list ────────────────────────────────────────────────────
    if (tab === 'purge') {
        return (
            <div>
                {error && <div className="cf-note red">{error}</div>}
                <Filters tab={tab} onTab={setTab} />

                {rows.length === 0 ? (
                    <div className="cf-empty">Nothing to purge. No withdrawn child had been exported.</div>
                ) : (
                    <table className="cf-measures">
                        <thead>
                            <tr><th>Code</th><th>Withdrawn</th><th>Reason</th><th>Bundles still holding it</th></tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.subject_id}>
                                    <td><b>{row.subject_id}</b></td>
                                    <td>{when(row.withdrawn_at)}</td>
                                    <td>{row.reason || '—'}</td>
                                    <td>
                                        {row.in_bundles.length === 0
                                            ? <span className="cf-hint">None — withdrew before any export</span>
                                            : row.in_bundles.join(', ')}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                <p className="cf-hint">
                    These children are already deleted from this system. A bundle that has gone out
                    cannot be reached from here, so each code stays listed until whoever holds that
                    copy removes it. The code is deliberately all that is left: nothing here can
                    turn it back into a name.
                </p>
            </div>
        );
    }

    // ── the queue ─────────────────────────────────────────────────────────
    return (
        <div>
            {message && <div className="cf-note green">{message}</div>}
            {error && <div className="cf-note red">{error}</div>}
            <Filters tab={tab} onTab={setTab} />

            {rows.length === 0 ? (
                <div className="cf-empty">
                    {tab === 'pending' ? 'No withdrawal requests waiting.' : 'No refused requests.'}
                </div>
            ) : (
                <table className="cf-measures">
                    <thead>
                        <tr><th>Asked</th><th>By</th><th>Reason</th><th /></tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.id}>
                                <td>{when(row.created_at)}</td>
                                <td>{row.requested_by_name || '—'}</td>
                                <td>
                                    {row.reason}
                                    {row.decision_note && (
                                        <div className="cf-hint">Refused: {row.decision_note}</div>
                                    )}
                                </td>
                                <td>
                                    {tab === 'pending' && (
                                        <button
                                            type="button" className="btn btn-sm btn-secondary"
                                            disabled={busy} onClick={() => open(row)}
                                        >
                                            Review…
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {tab === 'pending' && (
                <p className="cf-hint">
                    A request already stopped new recordings the day it was raised. What waits here is
                    deletion, which cannot be undone — so whoever asked does not decide it, and
                    nothing is approved without first seeing what would be removed.
                </p>
            )}
        </div>
    );
}

function Filters({ tab, onTab }) {
    return (
        <div className="cf-filters">
            {TABS.map((t) => (
                <button
                    key={t.key} type="button"
                    className={`cf-chip${tab === t.key ? ' active' : ''}`}
                    onClick={() => onTab(t.key)}
                >
                    {t.label}
                </button>
            ))}
        </div>
    );
}
